// Outer chrome shared by every login-flow page. 4px classification stripe at top,
// centered card on cobalt-charcoal canvas, tight density, no decoration.

import type { ReactNode } from 'react';

export function LoginShell({
  step,
  title,
  sub,
  children,
  footer,
}: {
  step: string; // e.g. "STEP 2 / 4 · CHANGE PASSWORD"
  title: string;
  sub?: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div
      className="eid"
      style={{
        height: '100vh',
        width: '100vw',
        background: 'var(--bg-base)',
        color: 'var(--fg-1)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <div className="class-stripe pub" />
      <main
        style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          overflowY: 'auto',
        }}
      >
        <div
          className="pane login-card"
          style={{
            width: 420,
            padding: 24,
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
          }}
        >
          {/* Wordmark */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 14 14"
              fill="none"
              stroke="var(--primary)"
              strokeWidth="1.2"
            >
              <path d="M1 7L7 1L13 7L7 13L1 7Z" />
              <path d="M4 7L7 4L10 7L7 10L4 7Z" />
            </svg>
            <span style={{ fontSize: 14, fontWeight: 700, letterSpacing: '1.6px' }}>EIDOLLON</span>
            <span className="meta mono" style={{ marginLeft: 'auto' }}>
              v0.1.0
            </span>
          </div>

          <div>
            <div className="meta mono upper" style={{ fontSize: 10 }}>
              {step}
            </div>
            <div className="title-l" style={{ marginTop: 2 }}>
              {title}
            </div>
            {sub && (
              <div className="meta" style={{ marginTop: 4, lineHeight: 1.5 }}>
                {sub}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>{children}</div>

          {footer && <div style={{ marginTop: 4 }}>{footer}</div>}
        </div>
      </main>
      <div
        className="meta mono"
        style={{
          height: 26,
          padding: '0 12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderTop: '1px solid var(--bd)',
          background: 'var(--bg-pane)',
        }}
      >
        <span>EIDOLLON · OPERATOR TERMINAL</span>
        <span className="login-footer-right">
          SESSION TLS 1.3 · MFA REQUIRED · NO PUBLIC SIGNUP
        </span>
      </div>
    </div>
  );
}

export function FormField({
  label,
  hint,
  children,
  mono,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  mono?: boolean;
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span className="lbl">{label}</span>
      <div
        style={{
          height: 32,
          background: 'var(--bg-base)',
          border: '1px solid var(--bd-strong)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 8px',
          fontFamily: mono ? 'var(--mono)' : 'var(--sans)',
        }}
      >
        {children}
      </div>
      {hint && (
        <span className="meta" style={{ fontSize: 10 }}>
          {hint}
        </span>
      )}
    </label>
  );
}

export function ErrorBanner({ code }: { code: string | null }) {
  if (!code) return null;
  const map: Record<string, string> = {
    invalid_credentials: 'Invalid email or password.',
    account_locked: 'Account temporarily locked. Try again later.',
    rate_limited: 'Too many attempts. Try again in a few minutes.',
    flow_expired: 'Login session expired. Please start again.',
    weak_password: 'Password too weak. Use 14+ chars, upper / lower / number / symbol.',
    invalid_totp: 'Invalid 6-digit code.',
    invalid_recovery_code: 'Invalid recovery code.',
    session_expired: 'Session expired. Please sign in again.',
    session_revoked: 'Session ended. Please sign in again.',
    invalid_request: 'Request rejected. Check your input.',
    internal_error: 'Something went wrong. Try again.',
  };
  const msg = map[code] ?? 'Login failed. Please try again.';
  return (
    <div
      style={{
        padding: '8px 10px',
        border: '1px solid var(--red-line)',
        background: 'var(--red-soft)',
        color: 'var(--red)',
        fontSize: 11,
        fontFamily: 'var(--mono)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span style={{ width: 6, height: 6, background: 'var(--red)' }} />
      {msg}
    </div>
  );
}
